import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolderPlus } from '@fortawesome/free-solid-svg-icons'
import { useCreateFolder } from 'api/hooks/use-folders'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { MAX_FOLDER_DEPTH } from 'dashboard/folders/folder-tree'

// `parentFolder` is the folder the index is currently showing, if any — a new folder is created
// inside it rather than at the top level, which is how the tree gets built. `atMaxDepth` says the
// folder is already MAX_FOLDER_DEPTH levels down, so it can hold items but no more subfolders.
const CreateFolderModal = ({ parentFolder = null, atMaxDepth = false }) => {
  const [show, setShow] = useState(false)
  const [name, setName] = useState('')
  const queryClient = useQueryClient()

  const { mutate: createFolder, isLoading } = useCreateFolder({
    options: {
      onSuccess: () => {
        setShow(false)
        setName('')
        queryClient.invalidateQueries('folders')
      },
      onError: () => {
        toast.error('Failed to create folder')
      }
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (name.trim()) {
      createFolder({ name: name.trim(), parent_id: parentFolder?.id || null })
    }
  }

  const label = parentFolder ? 'New Subfolder' : 'New Folder'
  const button = (
    <Button variant="white" onClick={() => setShow(true)} disabled={atMaxDepth}>
      <FontAwesomeIcon icon={faFolderPlus} className="me-1" />
      {label}
    </Button>
  )

  return (
    <>
      {atMaxDepth ? (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id="create-folder-max-depth">
              Folders nest up to {MAX_FOLDER_DEPTH} levels deep
            </Tooltip>
          }
        >
          <span>{button}</span>
        </OverlayTrigger>
      ) : (
        button
      )}
      <Modal show={show} onHide={() => { setShow(false); setName('') }}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{parentFolder ? `Create Folder in ${parentFolder.name}` : 'Create Folder'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Folder Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter folder name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="white" onClick={() => { setShow(false); setName('') }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!name.trim() || isLoading}>
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default CreateFolderModal
